import { GenericEntity, GithubProjectItemEntity } from "@cohesive/api-entities";
import { EntityCardSkeleton } from "@cohesive/ui-components";
import { AlertCircle } from "lucide-react";
import React, { useMemo } from "react";
import { EntityDragRow } from "../../../dnd/EntityDragRow";
import { SelectorEmpty } from "../../Selector";
import { CompactListItem } from "../components/CompactListItem";

interface ProjectItemsViewProps {
  items: GithubProjectItemEntity[];
  isLoading: boolean;
  issueState: "all" | "open" | "closed";
  searchQuery: string;
  onItemSelect: (item: any) => void;
  isFetchingNextPage?: boolean;
  genericById: Map<string, GenericEntity>;
}

export const ProjectItemsView: React.FC<ProjectItemsViewProps> = ({
  items,
  isLoading,
  issueState,
  searchQuery,
  onItemSelect,
  isFetchingNextPage,
  genericById,
}) => {
  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = [...items];

    // Filter by state
    if (issueState !== "all") {
      filtered = filtered.filter(
        (item: any) =>
          item.data.content_state?.toLowerCase() === issueState.toLowerCase(),
      );
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item: any) =>
          item.data.content_title?.toLowerCase().includes(query) ||
          item.data.content_body?.toLowerCase().includes(query),
      );
    }

    // Sort by updated_at (most recent first)
    filtered.sort((a, b) => {
      const aDate = new Date(a.data.updated_at || 0).getTime();
      const bDate = new Date(b.data.updated_at || 0).getTime();
      return bDate - aDate;
    });

    return filtered;
  }, [items, issueState, searchQuery]);

  const filteredItemIds = useMemo(
    () => filteredItems.map((i: any) => String(i.data.id)),
    [filteredItems],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <EntityCardSkeleton
            key={i}
            className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
          />
        ))}
      </div>
    );
  }

  if (filteredItems.length === 0) {
    return <SelectorEmpty message="No items found" />;
  }

  return (
    <>
      <div>
        {filteredItems.map((item: any) => {
          const title = item.data.content_number
            ? `#${item.data.content_number}: ${item.data.content_title}`
            : item.data.content_title;

          const subtitleParts = [];
          if (item.data.repository_name) {
            subtitleParts.push(
              `${item.data.repository_owner}/${item.data.repository_name}`,
            );
          }
          subtitleParts.push(item.data.content_state);
          const subtitle = subtitleParts.join(" • ");
          const generic = genericById.get(String(item.data.id));

          return (
            <EntityDragRow
              key={item.data.id}
              entity={item}
              generic={generic}
              entityId={String(item.data.id)}
              orderedIds={filteredItemIds}
              onSelect={() => onItemSelect(generic ?? item)}
              dragScope="github"
            >
              <CompactListItem
                icon={<AlertCircle className="h-4 w-4" />}
                title={title}
                subtitle={subtitle}
                webLink={item.data.content_url || item.data.web_link}
                onClick={() => onItemSelect(generic ?? item)}
                onAddToContext={() => onItemSelect(generic ?? item)}
              />
            </EntityDragRow>
          );
        })}
      </div>
      {isFetchingNextPage && (
        <div className="space-y-2 p-2">
          {[1, 2, 3].map((i) => (
            <EntityCardSkeleton
              key={`loading-${i}`}
              className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
            />
          ))}
        </div>
      )}
    </>
  );
};
